import { createFileRoute, Link } from "@tanstack/react-router";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { LazyTurnstile } from "@/components/site/LazyTurnstile";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { sendSuggestionInquiry } from "@/lib/inquiries.functions";

const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

export const Route = createFileRoute("/resources/suggestion")({
  head: () => ({
    meta: [
      { title: "Suggestion Box | Virginia Bariatric" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Share your feedback, ideas, or suggestions to help us improve care at Virginia Bariatric Surgery.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Suggestion Box | Virginia Bariatric" },
        { property: "og:description", content: "Share your feedback, ideas, or suggestions to help us improve care at Virginia Bariatric Surgery." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/resources/suggestion" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Suggestion Box | Virginia Bariatric" },
        { name: "twitter:description", content: "Share your feedback, ideas, or suggestions to help us improve care at Virginia Bariatric Surgery." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/resources/suggestion" }],
  }),
  component: SuggestionPage,
});

function SuggestionPage() {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitSuggestion = useServerFn(sendSuggestionInquiry);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the security check before sending.");
      return;
    }

    setSending(true);
    setError(null);

    try {
      await submitSuggestion({ data: { message: message.trim(), turnstileToken } });
      setSent(true);
      setMessage("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="text-center max-w-4xl mx-auto">
          <EditableText
            as="h1"
            contentKey="suggestion.hero.title"
            defaultValue="We Value Your Feedback"
            className="text-4xl md:text-5xl font-serif"
          />
          <EditableText
            as="p"
            contentKey="suggestion.hero.intro"
            defaultValue="Tell us how we can improve."
            className="mt-2 md:mt-3 text-foreground/80 leading-[1.6] text-base md:text-lg"
          />
        </div>
      </PageSection>

      <PageSection className="relative z-10 pt-2 pb-4 text-center">
        {sent ? (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <EditableText
              as="h2"
              contentKey="suggestion.thanks.title"
              defaultValue="Thank you"
              className="text-2xl font-serif"
            />
            <EditableText
              as="p"
              contentKey="suggestion.thanks.body"
              multiline
              defaultValue="Your suggestion has been sent. We appreciate your feedback."
              className="mt-2 text-muted-foreground"
            />
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-3 shadow-sm space-y-2 text-left"
          >
            <label htmlFor="suggestion-message" className="block text-sm">
              <span className="block mb-0.5 font-medium">
                Message<span aria-hidden="true" className="text-brand">*</span>
              </span>
              <textarea
                id="suggestion-message"
                required
                name="message"
                rows={4}
                maxLength={2000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand resize-y leading-[1.6]"
                placeholder="Tell us how we can improve..."
              />
            </label>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
<div className="flex justify-center">
              <LazyTurnstile
                siteKey={siteKey}
                triggerRef={formRef}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setError("Security check failed. Please try again.")}
                onExpire={() => setTurnstileToken(null)}
              />
            </div>
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={sending || !turnstileToken}
                className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                {sending ? "Sending..." : "Send suggestion"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Resources
          </Link>
        </div>
      </PageSection>
    </>
  );
}
